import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { AlertController, ModalController } from '@ionic/angular/standalone';
import { TranslatePipe, _ } from '@ngx-translate/core';
import { default as moment } from 'moment';
import { filter, first } from 'rxjs';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { CompanyModalComponent } from '../../../../modals/company-modal/company-modal.component';
import { Company } from '../../../../models/company';
import { Document } from '../../../../models/media';
import { User } from '../../../../models/user';
import { AuthService } from '../../../../services/auth.service';
import { CompanyService } from '../../../../services/company.service';
import { MediaService } from '../../../../services/media.service';
import { TranslateService } from '../../../../services/translate.service';
import { ValidationScopeWarningService } from '../../../../services/validation-scope-warning.service';
import { DocumentUtils } from '../../../../utils/document.utils';
import { LoadingUtils } from '../../../../utils/loading.utils';
import { DocumentExpiresAtValidator } from '../../../../validators/document.validator';

@Component({
    selector: 'app-profile-tab-company',
    templateUrl: './profile-tab-company.component.html',
    styleUrls: ['./profile-tab-company.component.scss'],
    imports: [CommonModule, TranslatePipe, LoaderComponent, ReactiveFormsModule],
})
export class ProfileTabCompanyComponent implements OnInit {
    user: User;
    company: Company;
    loaded = false;
    documents: Document[];
    documentTypes = DocumentUtils.DOCUMENT_TYPES;
    addDocumentForm = new FormGroup(
        {
            type: new FormControl('', [Validators.required]),
            expiresAt: new FormControl(''),
        },
        [DocumentExpiresAtValidator],
    );

    constructor(
        private readonly authService: AuthService,
        private readonly modalCtrl: ModalController,
        private readonly mediaService: MediaService,
        private readonly companyService: CompanyService,
        private readonly alertCtrl: AlertController,
        private readonly translateService: TranslateService,
        private readonly validationScopeWarningService: ValidationScopeWarningService,
    ) {}

    ngOnInit() {
        this.authService.userLoggedIn
            .pipe(
                filter((loggedIn) => !!loggedIn),
                first(),
            )
            .subscribe({
                next: (user) => {
                    this.user = user;
                    this.companyService.getMyCompany(user.id).subscribe({
                        next: (company) => {
                            this.company = company;
                            this.loaded = true;
                            this.refreshDocuments();
                        },
                    });
                },
            });
    }

    private refreshDocuments() {
        this.documents = null;
        this.mediaService.getDocuments(this.company?.id).subscribe({
            next: (documents) => {
                this.documents = documents;
                this.documentTypes = DocumentUtils.DOCUMENT_TYPES.filter((d) => {
                    if (d.categoryTags?.length) {
                        return false;
                    }

                    if (this.documents.find((doc) => doc.type === d.value)) {
                        return false;
                    }

                    return !d.companyActivities || d.companyActivities.includes(this.company.activity);
                });
            },
        });
    }

    async changeCompanyInfo() {
        const modal = await this.modalCtrl.create({
            component: CompanyModalComponent,
            componentProps: {
                company: this.company,
            },
        });
        modal.present();
        modal.onWillDismiss().then(() => {
            this.companyService.getMyCompany(this.user.id).subscribe({
                next: (company) => {
                    this.company = company;
                    this.refreshDocuments();
                },
            });
        });
    }

    displayExpiresAtField() {
        return DocumentUtils.requiresExpirationDate(this.addDocumentForm.get('type').value);
    }

    canAddDocuments() {
        return this.company && this.documents && this.documentTypes.length > 0;
    }

    async uploadDocument() {
        if (this.addDocumentForm.invalid) {
            return;
        }

        const confirmed = await this.validationScopeWarningService.confirmIfNeeded();
        if (!confirmed) {
            return;
        }

        const media = await this.mediaService.pickDocument('document');
        if (!media) {
            return;
        }

        const type = this.addDocumentForm.get('type').value;
        let expiresAt = DocumentUtils.requiresExpirationDate(type) ? this.addDocumentForm.get('expiresAt').value : null;
        if (expiresAt) {
            expiresAt = moment(expiresAt).endOf('day').format();
        }
        this.companyService.addDocument({ type, expiresAt, media: media.id }).subscribe({
            next: () => {
                LoadingUtils.hide();
                this.refreshDocuments();
                this.addDocumentForm.reset();
            },
        });
    }

    async downloadDocument(d: Document) {
        await LoadingUtils.show();

        this.mediaService.createDownload(d.media.id).subscribe({
            next: (download) => {
                LoadingUtils.hide();
                Browser.open({ url: download.url, windowName: '_system' });
            },
        });
    }

    getLabelFromType(type: string) {
        return DocumentUtils.getLabelFromType(type);
    }

    async deleteDocument(d: Document) {
        const user = this.user;
        const needsRevalidation = !!user?.pro && user.status === 'validated';
        const message = needsRevalidation
            ? `${this.translateService.get(_('Are you sure you want to delete this document?'))}\n\n${this.translateService.get(_('validation_scope_edit_warning'))}`
            : this.translateService.get(_('Are you sure you want to delete this document?'));

        const alert = await this.alertCtrl.create({
            header: this.translateService.get(_('Delete document')),
            message,
            buttons: [
                {
                    text: this.translateService.get(_('Cancel')),
                    role: 'cancel',
                    cssClass: 'secondary',
                },
                {
                    text: this.translateService.get(_('Delete')),
                    handler: async () => {
                        await LoadingUtils.show();
                        this.companyService.deleteDocument(d.id).subscribe({
                            next: () => {
                                this.refreshDocuments();
                                LoadingUtils.hide();
                            },
                        });
                    },
                },
            ],
        });
        alert.present();
    }
}
